// Debug sink -- the local vetting surface.
//
// Keeps the last ~200 enriched events in an in-memory ring buffer, exposes a
// pub/sub so the dev Event Inspector can render the live stream, and mirrors
// each event to console.debug as a compact styled line. Hung on
// window.__pmtEvents for manual poking from the console.
//
// The RECORDING is on in dev always (e2e and agent specs read window.__pmtEvents);
// in prod only with the debug flag (?debugEvents=1 or localStorage pmt.debug=1,
// analytics/debugFlag.ts). Showing it (the Event Inspector pill) needs the flag too.

import type { EnrichedEvent } from '../events';
import { debugFlagOn } from '../debugFlag';
import type { EventSink } from './types';

const RING_CAP = 200;
const ring: EnrichedEvent[] = [];
type Listener = (events: readonly EnrichedEvent[]) => void;
const listeners = new Set<Listener>();

function push(event: EnrichedEvent): void {
    ring.push(event);
    if (ring.length > RING_CAP) ring.splice(0, ring.length - RING_CAP);
    for (const l of listeners) {
        try {
            l(ring);
        } catch {
            // a bad listener must not break tracking
        }
    }
}

/** Current buffer (newest last). Copy so callers cannot mutate the ring. */
export function getEvents(): readonly EnrichedEvent[] {
    return ring.slice();
}

/** Subscribe to buffer changes; returns an unsubscribe fn. */
export function subscribe(listener: Listener): () => void {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
}

/** Clear the buffer (used by the inspector's Clear button). */
export function clearEvents(): void {
    ring.length = 0;
    for (const l of listeners) {
        try {
            l(ring);
        } catch {
            // ignore
        }
    }
}

function debugEnabled(): boolean {
    // Dev builds: always on. Prod builds: only with the debug flag.
    if (import.meta.env.DEV) return true;
    return debugFlagOn();
}

let installed = false;
function installWindowGlobal(): void {
    if (installed || typeof window === 'undefined') return;
    installed = true;
    Object.defineProperty(window, '__pmtEvents', {
        configurable: true,
        get: () => ring.slice(),
    });
}

export const debugSink: EventSink = {
    id: 'debug',
    accepts: () => debugEnabled(),
    send(event: EnrichedEvent) {
        installWindowGlobal();
        push(event);
        // the agent's events in italics: the console keeps no UI colour (spec--pmt-ui-redesign S9)
        const voice = event.source === 'agent' ? 'font-style:italic' : 'font-style:normal';
        // Compact single line: [source/surface] event {props}
        console.debug(
            `%c[pmt]%c ${event.source}/${event.surface} %c${event.event}`,
            'opacity:0.7',
            `${voice};font-weight:600`,
            'color:inherit;font-weight:600',
            event.props,
        );
    },
};
